import axios from "axios";

/**
 * Ghost Content API client.
 *
 * Reads from a self-hosted Ghost instance using the read-only Content API key.
 * Configure via env vars (see .env):
 *   REACT_APP_GHOST_URL      e.g. https://blog.blindnightrecords.com
 *   REACT_APP_GHOST_CONTENT_KEY  the Content API key from Ghost Admin > Integrations
 */

const GHOST_URL = (process.env.REACT_APP_GHOST_URL ?? "").replace(/\/$/, "");
const GHOST_KEY = process.env.REACT_APP_GHOST_CONTENT_KEY ?? "";
const GHOST_VERSION = "v5.0";

// What the rest of the app consumes. Mapped from Ghost's post shape.
export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  description: string; // Ghost excerpt
  html: string; // rendered post body
  thumbnailImgSrc: string | null; // feature_image
  thumbnailAltText: string;
  publishDate: Date;
  author: string;
  categories: string[]; // Ghost tag names
}

// Subset of Ghost's API response we rely on.
interface GhostTag {
  name: string;
}
interface GhostAuthor {
  name: string;
}
interface GhostPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  custom_excerpt: string | null;
  html: string | null;
  feature_image: string | null;
  feature_image_alt: string | null;
  published_at: string;
  primary_author: GhostAuthor | null;
  tags: GhostTag[] | null;
}

const contentApi = axios.create({
  baseURL: `${GHOST_URL}/ghost/api/content`,
  params: { key: GHOST_KEY },
});

// Short excerpt for blog tiles. Prefers a hand-written custom excerpt; otherwise
// trims Ghost's auto-excerpt to ~160 chars on a word boundary with an ellipsis.
const makeExcerpt = (post: GhostPost): string => {
  if (post.custom_excerpt) return post.custom_excerpt;
  const text = (post.excerpt ?? "").trim();
  if (text.length <= 160) return text;
  const cut = text.slice(0, 160);
  const lastSpace = cut.lastIndexOf(" ");
  return `${cut.slice(0, lastSpace > 0 ? lastSpace : 160).trimEnd()}…`;
};

const mapPost = (post: GhostPost): BlogPost => ({
  id: post.id,
  slug: post.slug,
  title: post.title,
  description: makeExcerpt(post),
  html: post.html ?? "",
  thumbnailImgSrc: post.feature_image,
  thumbnailAltText: post.feature_image_alt ?? post.title,
  publishDate: new Date(post.published_at),
  author: post.primary_author?.name ?? "Blind Night Records",
  categories: (post.tags ?? []).map((t) => t.name),
});

// Fetch all published posts (newest first).
export const getBlogs = async (): Promise<BlogPost[]> => {
  const response = await contentApi.get<{ posts: GhostPost[] }>("/posts/", {
    params: {
      include: "tags,authors",
      limit: "all",
      order: "published_at desc",
      "api-version": GHOST_VERSION,
    },
  });
  return response.data.posts.map(mapPost);
};

// Fetch a single post by its slug.
export const getBlogBySlug = async (slug: string): Promise<BlogPost> => {
  const response = await contentApi.get<{ posts: GhostPost[] }>(
    `/posts/slug/${slug}/`,
    {
      params: {
        include: "tags,authors",
        "api-version": GHOST_VERSION,
      },
    }
  );
  return mapPost(response.data.posts[0]);
};

// Distinct tag names across all posts, for the category filter.
export const getBlogCategories = async (): Promise<string[]> => {
  const response = await contentApi.get<{ tags: GhostTag[] }>("/tags/", {
    params: {
      limit: "all",
      filter: "visibility:public",
      "api-version": GHOST_VERSION,
    },
  });
  return response.data.tags.map((t) => t.name);
};
